/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { UIMode } from '@/constants/constants.ts';
import { ConfigENV } from '@/init';

interface ThemeContextProviderProps {
  children?: React.ReactNode | undefined;
  brandThemes?: ConfigENV['THEMES'];
}

interface ThemeContext {
  theme?: UIMode;
  toggleTheme?: () => void;
  brandThemes?: ConfigENV['THEMES'];
  logOut?: () => void;
}

const isProd = import.meta.env.PROD;

const changeTheme = ({ mode }: { mode: UIMode }) => {
  const themeLink = document.getElementById('theme-link') as HTMLLinkElement | null;
  if (themeLink) {
    const newHref = isProd ? `/themes/${mode}/theme.css` : `/themes/${mode}/theme.dev.css`;
    themeLink.href = newHref;
  }
};

// eslint-disable-next-line react-refresh/only-export-components
export const ThemeContext = React.createContext<ThemeContext>({} as ThemeContext);

const getStorageTheme = (): UIMode => {
  const storage = window.localStorage.getItem('theme') as UIMode;
  if (!storage) {
    return UIMode.light;
  }
  if (![UIMode.light, UIMode.dark].includes(storage)) {
    return UIMode.light;
  }
  return storage;
};

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children, brandThemes }) => {
  const [theme, setTheme] = useState<UIMode>(getStorageTheme());

  const getCss = (_theme = 'light') => {
    return [`./rabix/${_theme}/base.css`, `./rabix/${_theme}/port-drag.css`, `./rabix/${_theme}/selection.css`];
  };

  const switchTheme = useCallback((oldTheme: UIMode, newTheme: UIMode) => {
    if (oldTheme === newTheme) {
      return;
    }
    changeTheme({ mode: newTheme });
    setTheme(newTheme);
    window.localStorage.setItem('theme', newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === UIMode.dark ? UIMode.light : UIMode.dark;
    if (theme) {
      switchTheme(theme, newTheme);
    }
  }, [theme, switchTheme]);

  useEffect(() => {
    switchTheme(theme === UIMode.light ? UIMode.dark : UIMode.light, theme);

    const { origin, pathname, search } = window.location;
    const urlParams = new URLSearchParams(search);

    const redirect_pathname = urlParams.get('redirect');
    if (redirect_pathname) {
      let redirect_router = origin;
      if (pathname && pathname !== '/') {
        redirect_router = redirect_router.concat(pathname);
      }
      window.location.href = `${redirect_router}#${redirect_pathname}`;
    }
  }, [switchTheme, theme]);

  useEffect(() => {
    const links: HTMLLinkElement[] = [];
    getCss(theme ?? 'light').forEach((style) => {
      const link = document.createElement('link');
      link.type = 'text/css';
      link.rel = 'stylesheet';
      link.href = style;
      document.head.appendChild(link);
      links.push(link);
    });
    return () => {
      links.forEach((link) => {
        document.head.removeChild(link);
      });
    };
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        brandThemes
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within an ThemeContext');
  }
  return context;
};
